import { faArrowLeft, faCopy, faEdit, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faAddressBook } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  CloseButton,
  DestructiveModal,
  Divider,
  ErrorModal,
  Flex,
  Text,
  TextButton,
  Toast,
} from '@modusign/design-system';
import { useState } from 'react';

import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import {
  WindowModal,
  WindowModalContent,
  WindowModalHeader,
} from '@app/components/Modal/WindowModal';
import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import ContactCopyNameInputModal from '@app/features/addressBook/components/ContactCopyNameInputModal';
import ContactInputForm from '@app/features/addressBook/components/ContactInputForm';
import { ADDRESS_BOOK_ERROR_MESSAGE } from '@app/features/addressBook/constants';
import useContactsById from '@app/features/addressBook/hooks/useContactsById';
import useCreateContact from '@app/features/addressBook/hooks/useCreateContact';
import { CreateContactFormValue } from '@app/features/addressBook/types';
import josa from '@app/libs/josa';

import ContactInfo from './ContactInfo';

interface ContactManagementModalProps {
  contactId: string;
  isOpen: boolean;
  onClose: () => void;
  onCopyContactSucceeded: () => void;
  onDeleteSubmit: (contactId: string) => void;
  onUpdateSubmit: (formValues: CreateContactFormValue) => void;
}
function ContactManagementModal({
  contactId,
  isOpen,
  onClose,
  onCopyContactSucceeded,
  onDeleteSubmit,
  onUpdateSubmit,
}: ContactManagementModalProps) {
  const { data, error: dataLoadError } = useContactsById(contactId);

  const [isContactCopyNameInputModalOpen, setIsContactCopyNameInputModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const {
    error: createContactError,
    isLoading: isCreateContactLoading,
    mutate: createContact,
    reset: resetCreateContact,
  } = useCreateContact({
    onSuccess: () => {
      Toast.success({ description: '연락처 사본을 만들었습니다.' });
      onCopyContactSucceeded();
    },
  });

  if (!data) {
    if (dataLoadError) {
      return (
        <ErrorModal
          error={dataLoadError}
          errorMessageMapper={{
            ...GLOBAL_ERROR_MESSAGE,
            ...ADDRESS_BOOK_ERROR_MESSAGE,
          }}
          isOpen
          title="연락처 조회 실패"
          onClose={onClose}
        />
      );
    }
    return <GlobalLoadingSpinner />;
  }

  return (
    <>
      <WindowModal
        isOpen={isOpen}
        maxWidth="md"
        onRequestClose={isCreateContactLoading ? undefined : onClose}
      >
        <WindowModalHeader>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex>
              <Box color="textNeutralLight">
                <FontAwesomeIcon icon={faAddressBook} />
              </Box>
              <Box color="textNeutralLight" ml={2}>
                연락처
              </Box>
            </Flex>
            <Box>
              <CloseButton disabled={isCreateContactLoading} onClick={onClose} />
            </Box>
          </Flex>
        </WindowModalHeader>
        <WindowModalContent>
          {isEditing ? (
            <>
              <Box display={['none', 'block']} justifyContent="space-between" p="12px">
                <TextButton textDecoration="plain" onClick={() => setIsEditing(false)}>
                  <Box as="span" mr={2}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </Box>
                  관리
                </TextButton>
              </Box>
              <Divider mb={7} weight="medium" />
              <ContactInputForm
                contactId={data.id}
                defaultValues={{
                  ...data,
                  phoneNumber: data?.phoneNumber ? data.phoneNumber.slice(3) : null,
                }}
                submitButtonText="적용"
                onCancel={() => setIsEditing(false)}
                onSubmit={(formValues) => {
                  onUpdateSubmit(formValues);
                  onClose();
                }}
              />
            </>
          ) : (
            <>
              <Flex
                alignItems="center"
                display={['none', 'flex']}
                justifyContent="space-between"
                p="12px"
              >
                <Text color="textNeutralLight" fontWeight="medium">
                  관리
                </Text>
                <Flex gap={4} height="100%">
                  <TextButton
                    disabled={isCreateContactLoading}
                    role="button"
                    textDecoration="plain"
                    onClick={() => setIsDeleteConfirmOpen(true)}
                  >
                    <Box as="span" mr={1}>
                      <FontAwesomeIcon icon={faTrashCan} />
                    </Box>
                    삭제
                  </TextButton>
                  <Box height="100%">
                    <Divider direction="column" />
                  </Box>
                  <TextButton
                    disabled={isCreateContactLoading}
                    role="button"
                    textDecoration="plain"
                    onClick={() => {
                      const {
                        bizNumber,
                        companyName,
                        defaultForwardingMethod,
                        departmentPosition,
                        description,
                        email,
                        name,
                        phoneNumber,
                      } = data;

                      if (name.length > 25) {
                        setIsContactCopyNameInputModalOpen(true);
                        return;
                      }

                      createContact({
                        bizNumber,
                        companyName,
                        defaultForwardingMethod,
                        departmentPosition,
                        description,
                        email,
                        name: `${name} (사본)`,
                        phoneNumber,
                      });
                    }}
                  >
                    <Box as="span" mr={1}>
                      <FontAwesomeIcon icon={faCopy} />
                    </Box>
                    사본 만들기
                  </TextButton>
                  <TextButton
                    disabled={isCreateContactLoading}
                    role="button"
                    textDecoration="plain"
                    onClick={() => setIsEditing(true)}
                  >
                    <Box as="span" mr={1}>
                      <FontAwesomeIcon icon={faEdit} />
                    </Box>
                    수정
                  </TextButton>
                </Flex>
              </Flex>
              <Divider mb={7} weight="medium" />
              {data && <ContactInfo {...data} />}
            </>
          )}
        </WindowModalContent>
      </WindowModal>
      <DestructiveModal
        closeButtonLabel="취소"
        confirmButtonLabel="삭제"
        isOpen={isDeleteConfirmOpen}
        title="연락처 삭제"
        width="xs"
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={() => {
          onDeleteSubmit(contactId);
          onClose();
        }}
        onRequestClose={() => setIsDeleteConfirmOpen(false)}
      >
        <Text as="p" textAlign="center">
          {`'${josa(`${data?.name}'`, '을/를')}`} 삭제할까요?
          <br />
          삭제된 연락처는 복구할 수 없습니다.
        </Text>
      </DestructiveModal>
      {isContactCopyNameInputModalOpen && (
        <ContactCopyNameInputModal
          contactName={data.name}
          isOpen={isContactCopyNameInputModalOpen}
          onCancel={() => setIsContactCopyNameInputModalOpen(false)}
          onConfirm={(copyName) => {
            const {
              bizNumber,
              companyName,
              defaultForwardingMethod,
              departmentPosition,
              description,
              email,
              phoneNumber,
            } = data;

            createContact({
              bizNumber,
              companyName,
              defaultForwardingMethod,
              departmentPosition,
              description,
              email,
              name: copyName,
              phoneNumber,
            });
          }}
        />
      )}

      {createContactError && (
        <ErrorModal
          error={createContactError}
          errorMessageMapper={ADDRESS_BOOK_ERROR_MESSAGE}
          isOpen
          title="사본 만들기 실패"
          onClose={resetCreateContact}
        />
      )}
      {isCreateContactLoading && <GlobalLoadingSpinner />}
    </>
  );
}

export default ContactManagementModal;
